import { getDomain } from "tldts";

export type ValidateNavigableUrlResult =
  | { type: "success"; url: string }
  | { type: "error"; message: string };

const DOMAIN_MISMATCH_MESSAGE =
  "The domain in the url doesn't end with any of your configured root domains";

/**
 * Whether the input reads as a URL the merchant is typing rather than a search
 * term — the moment it starts with `http`. The Navigator uses this to stop
 * filtering the browse list while a URL is being entered (a URL isn't a search)
 * and to decide whether Enter is a navigation attempt at all.
 */
export function isUrlInput(value: string | undefined): boolean {
  return (value ?? "").trim().toLowerCase().startsWith("http");
}

/**
 * Validate an explicit navigation target typed into the Navigator.
 *
 * - Doesn't start with `http` → `undefined`: not a navigation attempt, so Enter
 *   does nothing here and the field's normal search/filter behavior stands.
 * - Starts with `http` but isn't a parseable http(s) URL → "Not a valid URL".
 * - A valid URL whose root domain isn't among the org's configured root domains
 *   (primary + additional domains) → the domain-mismatch error.
 * - A valid URL on a configured root domain → success with the normalized href.
 *
 * `configuredDomains` are the raw config values (full URLs or bare hosts); their
 * root domains are derived here so the caller just forwards the config.
 */
export function validateNavigableUrl(
  input: string,
  configuredDomains: string[]
): ValidateNavigableUrlResult | undefined {
  const trimmed = input.trim();
  if (!isUrlInput(trimmed)) {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { type: "error", message: "Not a valid URL" };
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return { type: "error", message: "Not a valid URL" };
  }

  const enteredRoot = getDomain(url.href);
  const configuredRoots = new Set(
    configuredDomains
      .map((domain) => getDomain(domain))
      .filter((root): root is string => root !== null)
  );

  if (enteredRoot === null || !configuredRoots.has(enteredRoot)) {
    return { type: "error", message: DOMAIN_MISMATCH_MESSAGE };
  }

  return { type: "success", url: url.href };
}
